import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  AddressSearchContainer,
  AddressInput,
  SuggestionsList,
  SuggestionItem,
  ConfidenceScore,
  SuggestionText,
  LoadingItem,
  NoResultsText,
} from './AddressLookup.styled';
import { ToggleAddressInputStyleButton } from '../../pages/InputPage/InputPage.styled';

const DEBOUNCE_TIME = 600;

const AddressLookup = ({
  addressData,
  onAddressSelect,
  onAddressChange,
  toggleAddressInputStyle,
  placeholder = 'Enter address...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [interactedWithInput, setInteractedWithInput] = useState(false);

  const debounce = useCallback((func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const constructCleanAddress = (osmResult) => {
    const addr = osmResult.address || {};
    const { house_number, road, city, town, village, state, postcode, county } =
      addr;

    const houseNumber = house_number || '';
    const street = road || '';
    const locality = city || town || village || '';
    const stateValue = state || '';
    const zip = postcode || '';

    if (!street) return null;

    let cleanAddress =
      houseNumber && street ? `${houseNumber} ${street}` : street;

    if (locality) cleanAddress += `, ${locality}`;
    if (stateValue) cleanAddress += `, ${stateValue}`;
    if (zip) cleanAddress += ` ${zip}`;

    return {
      fullAddress: cleanAddress,
      components: {
        houseNumber,
        street,
        city: locality,
        state: stateValue,
        postcode: zip,
      },
    };
  };

  const isValidAddress = (result) => {
    const addr = result.address || {};

    const hasStreet = addr.road || addr.street;
    const hasHouseNumber = addr.house_number;
    const hasCity = addr.city || addr.town || addr.village;
    const hasState = addr.state;
    const hasZip = addr.postcode;

    return hasStreet && hasHouseNumber && hasCity && hasState && hasZip;
  };

  const processOSMResults = (data) => {
    return data
      .filter(isValidAddress)
      .map((result) => {
        const cleanAddr = constructCleanAddress(result);
        if (!cleanAddr) return null;

        const { fullAddress, components } = cleanAddr;

        if (
          !components.street ||
          !components.houseNumber ||
          !components.city ||
          !components.state ||
          !components.postcode
        ) {
          return null;
        }

        const streetAddress = `${components.houseNumber} ${components.street}`;

        return {
          address: fullAddress,
          displayAddress: fullAddress,
          components,
          streetAddress,
          source: 'openstreetmap',
          importance: parseFloat(result.importance || 0.5),
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.importance - a.importance)
      .slice(0, 5);
  };

  const fetchOpenStreetMapSuggestions = async (address) => {
    if (!address.trim() || address.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setShowSuggestions(true);

    try {
      const params = new URLSearchParams({
        q: address,
        format: 'json',
        addressdetails: '1',
        limit: '10',
        countrycodes: 'us',
        'accept-language': 'en',
      });

      const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;

      const response = await axios.get(url, {
        headers: {
          'User-Agent': 'AddressLookup/1.0',
        },
      });

      if (response.data && response.data.length > 0) {
        const processedAddresses = processOSMResults(response.data);
        setSuggestions(processedAddresses);
        setShowSuggestions(processedAddresses.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching OpenStreetMap suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAddressInputs = () => {
    console.log('clearing address inputs');

    if (onAddressChange) {
      onAddressChange({ state: '', city: '', zip: '', streetAddress: '' });
    }
    setSuggestions([]);
  };

  const debouncedFetch = useCallback(
    debounce(fetchOpenStreetMapSuggestions, DEBOUNCE_TIME),
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedFetch(value);
    clearAddressInputs();
  };

  const handleSuggestionClick = (candidate) => {
    setInputValue(candidate.displayAddress);
    setShowSuggestions(false);
    setSuggestions([]);

    const addressData = {
      state: candidate.components.state || '',
      city: candidate.components.city || '',
      zip: candidate.components.postcode || '',
      streetAddress: candidate.streetAddress || '',
    };

    if (onAddressChange) {
      onAddressChange(addressData);
    }

    if (onAddressSelect) {
      onAddressSelect(addressData);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleInputFocus = () => {
    if (!interactedWithInput) setInteractedWithInput(true);
    if (suggestions.length > 0 || isLoading) {
      setShowSuggestions(true);
    }
  };

  useEffect(() => {
    if (!addressData) return '';

    function formatAddress(addressData) {
      const { streetAddress, city, state, zip } = addressData;

      const parts = [];
      if (streetAddress) parts.push(streetAddress.trim());
      if (city) {
        if (parts.length > 0) parts[parts.length - 1] += ',';
        parts.push(city.trim());
      }
      if (state && parts.length > 0) {
        if (parts.length > 0) parts[parts.length - 1] += ',';
        parts.push(state.trim());
      }
      if (zip) parts.push(zip.trim());

      return parts.join(' ');
    }

    let formattedAddress = formatAddress(addressData);

    if (formattedAddress.length > 0) {
      setInputValue(formattedAddress.trim());
    }
  }, []);

  const renderSuggestionsList = () => {
    if (!showSuggestions) return null;

    return (
      <SuggestionsList>
        {isLoading && (
          <LoadingItem>
            <SuggestionText>
              <div>Searching addresses...</div>
            </SuggestionText>
          </LoadingItem>
        )}

        {!isLoading &&
          suggestions.map((candidate, index) => (
            <SuggestionItem
              key={index}
              onClick={() => handleSuggestionClick(candidate)}
            >
              <SuggestionText>
                <div>{candidate.displayAddress}</div>
                <ConfidenceScore>Complete Address</ConfidenceScore>
              </SuggestionText>
            </SuggestionItem>
          ))}
      </SuggestionsList>
    );
  };

  const addressDataMissing =
    !addressData?.streetAddress || !addressData?.city || !addressData?.zip;

  return (
    <AddressSearchContainer>
      <AddressInput
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        placeholder={placeholder}
      />
      {renderSuggestionsList()}
      {!isLoading &&
      suggestions.length === 0 &&
      inputValue.length >= 3 &&
      addressDataMissing &&
      interactedWithInput ? (
        <NoResultsText>
          No complete addresses found. Please include street number, city,
          state, and ZIP or{' '}
          <ToggleAddressInputStyleButton
            onClick={() => toggleAddressInputStyle()}
          >
            click here
          </ToggleAddressInputStyleButton>{' '}
          to input manually.
        </NoResultsText>
      ) : (
        addressDataMissing && (
          <NoResultsText>
            Lookup the property address in the seach bar above or{' '}
            <ToggleAddressInputStyleButton
              onClick={() => toggleAddressInputStyle()}
            >
              input manually
            </ToggleAddressInputStyleButton>
            .
          </NoResultsText>
        )
      )}
    </AddressSearchContainer>
  );
};

export default AddressLookup;
